import {
  SceneLoader,
  TransformNode,
  Vector3,
  Quaternion,
  PointerEventTypes,
} from "@babylonjs/core";
import "@babylonjs/loaders";

/**
 * ------------------------------------------------------------
 * Component-local robot assets
 * ------------------------------------------------------------
 *
 * 이 파일과 같은 component 폴더 하위에 아래 구조가 있다고 가정:
 *
 * robot/
 *   ur3e.glb
 *   ur3e_joints.json
 *   motion.csv
 *   descriptions.txt
 *
 * - ur3e.glb: base/shoulder/upperarm/forearm/wrist1/wrist2/wrist3 계층이 이미 구성된 단일 GLB
 * - ur3e_joints.json: Babylon 기준 jointName -> childNode/axis 매핑
 * - motion.csv: timestamp_sec + *_joint_pos(rad) 기반 실제 모션 데이터
 * - descriptions.txt: motion.csv 설명 파일. 런타임 필수는 아니지만 metadata/debug 정보로 보관
 */
const COMPONENT_ROBOT_ASSETS = import.meta.glob("./robot/**/*", {
  eager: true,
  query: "?url",
  import: "default",
});

function normalizeRobotAssetKey(path) {
  const clean = String(path ?? "")
    .trim()
    .replace(/\\/g, "/")
    .replace(/^\.?\//, "");

  if (clean.startsWith("robot/")) {
    return `./${clean}`;
  }

  return `./robot/${clean}`;
}

function resolveComponentRobotAsset(path) {
  const key = normalizeRobotAssetKey(path);
  const url = COMPONENT_ROBOT_ASSETS[key];

  if (url) {
    return url;
  }

  // dev fallback. build 시에는 glob에 잡히는 파일만 안정적으로 포함됨.
  return new URL(key, import.meta.url).href;
}

const DEFAULT_ROBOT_GLB_URL = resolveComponentRobotAsset("ur3e.glb");
const DEFAULT_JOINTS_JSON_URL = resolveComponentRobotAsset("ur3e_joints.json");
const DEFAULT_MOTION_CSV_URL = resolveComponentRobotAsset("motion.csv");
const DEFAULT_DESCRIPTIONS_URL = resolveComponentRobotAsset("descriptions.txt");

/**
 * UR3e 주요 제어 조인트
 */
const CONTROLLABLE_JOINTS = [
  "shoulder_pan_joint",
  "shoulder_lift_joint",
  "elbow_joint",
  "wrist_1_joint",
  "wrist_2_joint",
  "wrist_3_joint",
];

const CSV_MOTION_JOINT_COLUMNS = {
  shoulder_pan_joint: "shoulder_pan_joint_pos",
  shoulder_lift_joint: "shoulder_lift_joint_pos",
  elbow_joint: "elbow_joint_pos",
  wrist_1_joint: "wrist_1_joint_pos",
  wrist_2_joint: "wrist_2_joint_pos",
  wrist_3_joint: "wrist_3_joint_pos",
};

const DEFAULT_JOINT_LIMITS = {
  shoulder_pan_joint: { lower: -Math.PI * 2, upper: Math.PI * 2 },
  shoulder_lift_joint: { lower: -Math.PI * 2, upper: Math.PI * 2 },
  elbow_joint: { lower: -Math.PI, upper: Math.PI },
  wrist_1_joint: { lower: -Math.PI * 2, upper: Math.PI * 2 },
  wrist_2_joint: { lower: -Math.PI * 2, upper: Math.PI * 2 },
  wrist_3_joint: { lower: null, upper: null },
};

function toVector3(value, fallback = new Vector3(0, 0, 0)) {
  if (value == null) return fallback.clone();
  if (value instanceof Vector3) return value.clone();

  if (Array.isArray(value)) {
    return new Vector3(
      Number(value[0] ?? fallback.x),
      Number(value[1] ?? fallback.y),
      Number(value[2] ?? fallback.z)
    );
  }

  if (typeof value === "object") {
    return new Vector3(
      Number(value.x ?? fallback.x),
      Number(value.y ?? fallback.y),
      Number(value.z ?? fallback.z)
    );
  }

  return fallback.clone();
}

function splitUrl(fullUrl) {
  const normalized = String(fullUrl);
  const idx = normalized.lastIndexOf("/");

  if (idx < 0) {
    return { rootUrl: "", fileName: normalized };
  }

  return {
    rootUrl: normalized.slice(0, idx + 1),
    fileName: normalized.slice(idx + 1),
  };
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}

function rand(min, max) {
  return Math.random() * (max - min) + min;
}

function normalizeAxis(axis) {
  const v = toVector3(axis, new Vector3(0, 1, 0));

  if (v.lengthSquared() === 0) {
    return new Vector3(0, 1, 0);
  }

  return v.normalize();
}

function ensureRotationQuaternion(node) {
  if (!node) return Quaternion.Identity();

  if (!node.rotationQuaternion) {
    node.rotationQuaternion = Quaternion.FromEulerAngles(
      Number(node.rotation?.x ?? 0),
      Number(node.rotation?.y ?? 0),
      Number(node.rotation?.z ?? 0)
    );
    node.rotation?.set?.(0, 0, 0);
  }

  return node.rotationQuaternion.clone();
}

function isAbsoluteUrl(url) {
  return /^(https?:|blob:|data:)/i.test(String(url ?? ""));
}

function resolveUrlMaybeComponentLocal(urlOrPath) {
  const value = String(urlOrPath ?? "").trim();

  if (!value) return "";

  if (isAbsoluteUrl(value)) {
    return value;
  }

  if (value.startsWith("/")) {
    return new URL(value, window.location.origin).href;
  }

  return resolveComponentRobotAsset(value);
}

async function fetchText(url, { optional = false } = {}) {
  const absoluteUrl = resolveUrlMaybeComponentLocal(url);

  try {
    const res = await fetch(absoluteUrl);

    if (!res.ok) {
      throw new Error(`파일을 불러오지 못했습니다: ${absoluteUrl} (${res.status})`);
    }

    return await res.text();
  } catch (error) {
    if (optional) return "";
    throw error;
  }
}

async function fetchJson(url) {
  const text = await fetchText(url);
  return JSON.parse(text);
}

function createAnimationProfile(name, mission) {
  const key = String(name ?? mission ?? "default").toLowerCase();

  if (key.includes("welding")) {
    return {
      shoulder_pan_joint: [-0.5, 0.5],
      shoulder_lift_joint: [-1.6, -0.2],
      elbow_joint: [0.3, 1.8],
      wrist_1_joint: [-1.0, 1.0],
      wrist_2_joint: [-1.2, 1.2],
      wrist_3_joint: [-1.5, 1.5],
      intervalMin: 500,
      intervalMax: 900,
      smoothing: 3.5,
    };
  }

  if (key.includes("inspection")) {
    return {
      shoulder_pan_joint: [-1.2, 1.2],
      shoulder_lift_joint: [-1.4, -0.4],
      elbow_joint: [0.2, 1.5],
      wrist_1_joint: [-1.4, 1.4],
      wrist_2_joint: [-1.4, 1.4],
      wrist_3_joint: [-Math.PI, Math.PI],
      intervalMin: 900,
      intervalMax: 1600,
      smoothing: 2.2,
    };
  }

  if (key.includes("pick")) {
    return {
      shoulder_pan_joint: [-1.0, 1.0],
      shoulder_lift_joint: [-1.7, -0.1],
      elbow_joint: [0.2, 1.8],
      wrist_1_joint: [-1.5, 1.5],
      wrist_2_joint: [-1.5, 1.5],
      wrist_3_joint: [-Math.PI, Math.PI],
      intervalMin: 480,
      intervalMax: 1100,
      smoothing: 3.1,
    };
  }

  return {
    shoulder_pan_joint: [-1.0, 1.0],
    shoulder_lift_joint: [-1.8, -0.1],
    elbow_joint: [0.1, 1.9],
    wrist_1_joint: [-1.8, 1.8],
    wrist_2_joint: [-1.8, 1.8],
    wrist_3_joint: [-Math.PI, Math.PI],
    intervalMin: 600,
    intervalMax: 1200,
    smoothing: 2.8,
  };
}

function parseCsvLine(line) {
  const result = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];

    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
        continue;
      }

      inQuotes = !inQuotes;
      continue;
    }

    if (char === "," && !inQuotes) {
      result.push(current);
      current = "";
      continue;
    }

    current += char;
  }

  result.push(current);

  return result.map((item) => item.trim());
}

function parseMotionCsv(csvText) {
  const lines = String(csvText ?? "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length < 2) {
    throw new Error("motion.csv에 데이터가 없습니다.");
  }

  const headers = parseCsvLine(lines[0]);
  const indexByName = new Map();

  headers.forEach((header, index) => {
    indexByName.set(header, index);
  });

  const timestampIndex = indexByName.get("timestamp_sec");

  if (timestampIndex == null) {
    throw new Error("motion.csv에 timestamp_sec 컬럼이 없습니다.");
  }

  Object.values(CSV_MOTION_JOINT_COLUMNS).forEach((columnName) => {
    if (!indexByName.has(columnName)) {
      throw new Error(`motion.csv에 ${columnName} 컬럼이 없습니다.`);
    }
  });

  const rawFrames = [];

  for (let i = 1; i < lines.length; i += 1) {
    const values = parseCsvLine(lines[i]);
    const timestampSec = Number(values[timestampIndex]);

    if (!Number.isFinite(timestampSec)) continue;

    const pose = {};

    Object.entries(CSV_MOTION_JOINT_COLUMNS).forEach(([jointName, columnName]) => {
      const columnIndex = indexByName.get(columnName);
      const angle = Number(values[columnIndex]);

      if (Number.isFinite(angle)) {
        pose[jointName] = angle;
      }
    });

    rawFrames.push({ timestampSec, pose });
  }

  if (!rawFrames.length) {
    throw new Error("motion.csv에서 유효한 프레임을 찾지 못했습니다.");
  }

  const startTime = rawFrames[0].timestampSec;

  return rawFrames.map((frame, index) => ({
    index,
    time: Math.max(0, frame.timestampSec - startTime),
    pose: frame.pose,
  }));
}

function findMotionFrameIndex(frames, time) {
  if (!frames.length) return 0;

  let low = 0;
  let high = frames.length - 1;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);

    if (frames[mid].time < time) {
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  return Math.max(0, Math.min(frames.length - 1, low));
}

function interpolatePose(frameA, frameB, time) {
  if (!frameA) return {};
  if (!frameB) return { ...frameA.pose };

  const duration = frameB.time - frameA.time;

  if (duration <= 0) {
    return { ...frameA.pose };
  }

  const t = clamp((time - frameA.time) / duration, 0, 1);
  const pose = {};

  Object.keys(CSV_MOTION_JOINT_COLUMNS).forEach((jointName) => {
    const a = Number(frameA.pose[jointName] ?? 0);
    const b = Number(frameB.pose[jointName] ?? a);
    pose[jointName] = lerp(a, b, t);
  });

  return pose;
}

function getTopLevelImportedNodes(result) {
  const importedNodes = [
    ...(result.transformNodes ?? []),
    ...(result.meshes ?? []),
  ].filter(Boolean);

  const importedSet = new Set(importedNodes);

  return importedNodes.filter((node) => {
    return !node.parent || !importedSet.has(node.parent);
  });
}

function collectImportedNodes(result) {
  return [
    ...(result.transformNodes ?? []),
    ...(result.meshes ?? []),
  ].filter(Boolean);
}

function findImportedNodeByName(result, name) {
  const allNodes = collectImportedNodes(result);

  return (
    allNodes.find((node) => node.name === name) ??
    allNodes.find((node) => String(node.name ?? "").split(".")[0] === name) ??
    null
  );
}

function parseJointConfig(jointsJson) {
  const joints = Array.isArray(jointsJson?.joints) ? jointsJson.joints : [];
  const links = Array.isArray(jointsJson?.links) ? jointsJson.links : [];

  return {
    raw: jointsJson,
    links,
    joints: joints.map((joint) => {
      const jointName = joint.jointName ?? joint.name;
      const limit = joint.limit ?? DEFAULT_JOINT_LIMITS[jointName] ?? { lower: null, upper: null };

      return {
        jointName,
        parentNode: joint.parentNode ?? joint.parent,
        childNode: joint.childNode ?? joint.child,
        type: joint.jointType ?? joint.type ?? "revolute",
        axis: joint.axisBabylon ?? joint.axis ?? [0, 1, 0],
        limit: {
          lower: Number.isFinite(Number(limit.lower)) ? Number(limit.lower) : null,
          upper: Number.isFinite(Number(limit.upper)) ? Number(limit.upper) : null,
        },
        sourceGlb: joint.sourceGlb ?? null,
        originUrdf: joint.originUrdf ?? null,
      };
    }),
  };
}

export async function createRobotArmBinding(scene, options = {}) {
  if (!scene) {
    throw new Error("createRobotArmBinding: scene가 필요합니다.");
  }

  const {
    id = "robot-arm-01",
    rootName = "ur3eRoot",

    rootPosition = { x: 10, y: 0.4, z: 5 },
    rootScaling = { x: 1, y: 1, z: 1 },
    rootRotation = { x: 0, y: 0, z: 0 },

    /**
     * 새 기본 구조:
     * robot/ur3e.glb
     * robot/ur3e_joints.json
     * robot/motion.csv
     * robot/descriptions.txt
     */
    robotGlbUrl = DEFAULT_ROBOT_GLB_URL,
    jointsJsonUrl = DEFAULT_JOINTS_JSON_URL,
    motionCsvUrl = DEFAULT_MOTION_CSV_URL,
    descriptionsUrl = DEFAULT_DESCRIPTIONS_URL,

    // 기존 options 호환용. 단일 GLB 방식에서는 collision은 기본적으로 비어 있음.
    enableVisual = true,
    enableCollision = false,
    collisionVisible = false,

    equipId = id ?? "robot-arm-01",
    equipName = "로봇암 01",
    isEquipment = true,
    statusText = "정상",
    previewUrl = null,
    temperature = "29℃",
    battery = "AC",
    output = 148,
    mode: initialMode = "Auto",
    mission: initialMission = "Pick & Place",
    percent = 100,

    animation = null,
    enablePicking = false,
    enableKeyboard = true,
    disablePointerMovePicking = true,
  } = options;

  const root = new TransformNode(rootName, scene);
  root.position = toVector3(rootPosition);
  root.scaling = toVector3(rootScaling, new Vector3(1, 1, 1));
  root.rotation = toVector3(rootRotation);

  let disposed = false;
  let pointerObserver = null;

  let currentMode = initialMode;
  let currentMission = initialMission;
  let currentAnimationName = animation?.name ?? initialMission ?? "Default";
  let currentAnimationSpeed = Math.max(0.1, Number(animation?.speed ?? 1));
  let currentAnimationLoop = animation?.loop ?? true;

  let randomMotionRunning = false;
  let randomMotionPaused = false;
  let randomMotionRaf = null;
  let randomMotionTimer = null;
  let currentAnimationHandle = null;

  let csvMotionFrames = null;
  let csvMotionRunning = false;
  let csvMotionPaused = false;
  let csvMotionRaf = null;
  let csvMotionStartedAt = 0;
  let csvMotionPausedAt = 0;
  let csvMotionSpeed = 1;
  let csvMotionLoop = true;

  const loadedMeshes = [];
  const visualMeshes = [];
  const collisionMeshes = [];

  const linkNodes = new Map();
  const jointRuntime = new Map();

  const equipmentMeta = {
    id,
    equipId,
    equipName,
    isEquipment,
    statusText,
    previewUrl,
    temperature,
    battery,
    output,
    mode: currentMode,
    mission: currentMission,
    percent,
    type: "robotArm",
  };

  if (disablePointerMovePicking) {
    scene.skipPointerMovePicking = true;
  }

  const [jointsJson, descriptionsText] = await Promise.all([
    fetchJson(jointsJsonUrl),
    fetchText(descriptionsUrl, { optional: true }),
  ]);

  const jointConfig = parseJointConfig(jointsJson);

  let importResult = { meshes: [], transformNodes: [] };

  if (enableVisual) {
    const absoluteGlbUrl = resolveUrlMaybeComponentLocal(robotGlbUrl);
    const { rootUrl, fileName } = splitUrl(absoluteGlbUrl);
    importResult = await SceneLoader.ImportMeshAsync("", rootUrl, fileName, scene);

    const topLevelNodes = getTopLevelImportedNodes(importResult);
    topLevelNodes.forEach((node) => {
      node.parent = root;
    });

    const importedMeshes = (importResult.meshes ?? []).filter(
      (mesh) => mesh && mesh.name !== "__root__"
    );

    importedMeshes.forEach((mesh) => {
      mesh.isPickable = enablePicking;
      mesh.metadata = {
        ...equipmentMeta,
        assetMode: "visual",
        partName: mesh.name,
      };

      loadedMeshes.push(mesh);
      visualMeshes.push(mesh);
    });
  }

  // GLB 내부 링크 노드를 JSON 기준으로 찾는다.
  // 링크명은 사용자가 요청한 대로 GLB 파일명 기준: base/shoulder/upperarm/forearm/wrist1/wrist2/wrist3.
  jointConfig.links.forEach((linkName) => {
    const node = findImportedNodeByName(importResult, linkName);
    if (node) {
      linkNodes.set(linkName, node);
      node.metadata = {
        ...(node.metadata ?? {}),
        ...equipmentMeta,
        linkName,
        assetMode: "link-node",
      };
    }
  });

  jointConfig.joints.forEach((joint) => {
    const childNode = linkNodes.get(joint.childNode) ?? findImportedNodeByName(importResult, joint.childNode);

    if (!joint.jointName || !childNode) {
      return;
    }

    if (!linkNodes.has(joint.childNode)) {
      linkNodes.set(joint.childNode, childNode);
    }

    const initialPosition = childNode.position?.clone?.() ?? Vector3.Zero();
    const initialRotationQuaternion = ensureRotationQuaternion(childNode);
    const axis = normalizeAxis(joint.axis);

    jointRuntime.set(joint.jointName, {
      name: joint.jointName,
      type: joint.type,
      parentNodeName: joint.parentNode,
      childNodeName: joint.childNode,
      childNode,
      motionNode: childNode,
      originNode: childNode.parent ?? root,
      initialPosition,
      initialRotationQuaternion,
      axis,
      limit: joint.limit,
      currentAngle: 0,
      targetAngle: 0,
      sourceGlb: joint.sourceGlb,
      originUrdf: joint.originUrdf,
    });
  });

  const visualDebugInfo = {
    mode: "single-hierarchical-glb-and-joints-json",
    robotGlbUrl: resolveUrlMaybeComponentLocal(robotGlbUrl),
    jointsJsonUrl: resolveUrlMaybeComponentLocal(jointsJsonUrl),
    motionCsvUrl: resolveUrlMaybeComponentLocal(motionCsvUrl),
    descriptionsUrl: resolveUrlMaybeComponentLocal(descriptionsUrl),
    descriptionsText,
    coordinateSystem: jointsJson?.coordinateSystem ?? "Babylon/glTF Y-up",
    linkNodes: [...linkNodes.keys()],
    jointNames: [...jointRuntime.keys()],
    missingJoints: CONTROLLABLE_JOINTS.filter((jointName) => !jointRuntime.has(jointName)),
    topLevelNodeNames: getTopLevelImportedNodes(importResult).map((node) => node.name),
    note: "robot/ur3e.glb의 계층형 링크 노드를 robot/ur3e_joints.json 기준으로 제어합니다.",
  };

  function updateMeshMetadata() {
    loadedMeshes.forEach((mesh) => {
      if (!mesh) return;

      mesh.metadata = {
        ...(mesh.metadata ?? {}),
        mode: currentMode,
        mission: currentMission,
        animationName: currentAnimationName,
      };
    });
  }

  function applyJointAngle(jointName, angle) {
    const runtime = jointRuntime.get(jointName);
    if (!runtime) return 0;

    let nextAngle = Number(angle) || 0;

    if (runtime.type !== "continuous" && runtime.type !== "revolute") {
      nextAngle = 0;
    }

    if (runtime.type === "revolute") {
      if (runtime.limit.lower != null) {
        nextAngle = Math.max(runtime.limit.lower, nextAngle);
      }

      if (runtime.limit.upper != null) {
        nextAngle = Math.min(runtime.limit.upper, nextAngle);
      }
    }

    runtime.currentAngle = nextAngle;

    const deltaQuaternion = Quaternion.RotationAxis(runtime.axis, nextAngle);

    // GLB에 저장된 초기 local rotation을 보존한 뒤, joint axis 회전만 추가한다.
    // 즉, T_origin * R_initial * R_joint(angle) 형태가 되도록 한다.
    runtime.childNode.rotationQuaternion = runtime.initialRotationQuaternion.multiply(deltaQuaternion);

    return nextAngle;
  }

  function setJointAngle(jointName, angle) {
    return applyJointAngle(jointName, angle);
  }

  function rotateJoint(jointName, delta) {
    const runtime = jointRuntime.get(jointName);
    if (!runtime) return 0;

    return applyJointAngle(jointName, runtime.currentAngle + delta);
  }

  function getPose() {
    const pose = {};

    CONTROLLABLE_JOINTS.forEach((jointName) => {
      pose[jointName] = jointRuntime.get(jointName)?.currentAngle ?? 0;
    });

    return pose;
  }

  function setPose(pose = {}) {
    Object.entries(pose).forEach(([jointName, angle]) => {
      if (jointRuntime.has(jointName)) {
        applyJointAngle(jointName, angle);
      }
    });
  }

  function resetPose() {
    CONTROLLABLE_JOINTS.forEach((jointName) => {
      applyJointAngle(jointName, 0);
    });
  }

  function focusTarget() {
    const camera = scene.activeCamera;

    if (!camera || typeof camera.setTarget !== "function") return;

    camera.setTarget(root.getAbsolutePosition());
  }

  function select() {
    scene.metadata = {
      ...(scene.metadata || {}),
      selectedRobotArmId: equipId,
    };
  }

  function setMode(nextMode) {
    currentMode = nextMode ?? currentMode;
    updateMeshMetadata();

    return currentMode;
  }

  function setMission(nextMission) {
    currentMission = nextMission ?? currentMission;
    updateMeshMetadata();

    return currentMission;
  }

  function setAnimationSpeed(speed = 1) {
    currentAnimationSpeed = Math.max(0.1, Number(speed || 1));

    return currentAnimationSpeed;
  }

  function setCollisionVisible(visible = true) {
    collisionMeshes.forEach((mesh) => {
      if (mesh) {
        mesh.isVisible = Boolean(visible);
      }
    });
  }

  function setCollisionEnabled(enabled = true) {
    collisionMeshes.forEach((mesh) => {
      if (mesh) {
        mesh.checkCollisions = Boolean(enabled);
      }
    });
  }

  function getVisualMeshes() {
    return [...visualMeshes];
  }

  function getCollisionMeshes() {
    return [...collisionMeshes];
  }

  function scheduleNextTargets(profile) {
    if (randomMotionTimer) {
      clearTimeout(randomMotionTimer);
      randomMotionTimer = null;
    }

    CONTROLLABLE_JOINTS.forEach((jointName) => {
      const runtime = jointRuntime.get(jointName);
      const range = profile[jointName];

      if (!runtime || !range) return;

      let target = rand(range[0], range[1]);

      if (runtime.type === "revolute") {
        if (runtime.limit.lower != null) {
          target = Math.max(runtime.limit.lower, target);
        }

        if (runtime.limit.upper != null) {
          target = Math.min(runtime.limit.upper, target);
        }
      }

      runtime.targetAngle = target;
    });

    const delay = rand(profile.intervalMin, profile.intervalMax) / currentAnimationSpeed;

    randomMotionTimer = setTimeout(() => {
      if (!disposed && randomMotionRunning && !randomMotionPaused) {
        scheduleNextTargets(profile);
      }
    }, delay);
  }

  function animateRandomMotion(now) {
    if (disposed || !randomMotionRunning) return;

    const last = animateRandomMotion._last || now;
    const dt = Math.min(0.05, (now - last) / 1000);
    animateRandomMotion._last = now;

    if (!randomMotionPaused) {
      const profile = createAnimationProfile(currentAnimationName, currentMission);
      const t = clamp(dt * profile.smoothing * currentAnimationSpeed, 0.02, 0.2);

      CONTROLLABLE_JOINTS.forEach((jointName) => {
        const runtime = jointRuntime.get(jointName);
        if (!runtime) return;

        const nextAngle = lerp(runtime.currentAngle, runtime.targetAngle, t);
        applyJointAngle(jointName, nextAngle);
      });
    }

    randomMotionRaf = requestAnimationFrame(animateRandomMotion);
  }

  function stopMotionLoop() {
    randomMotionRunning = false;
    randomMotionPaused = false;
    animateRandomMotion._last = 0;

    if (randomMotionTimer) {
      clearTimeout(randomMotionTimer);
      randomMotionTimer = null;
    }

    if (randomMotionRaf) {
      cancelAnimationFrame(randomMotionRaf);
      randomMotionRaf = null;
    }
  }

  async function loadCsvMotionFrames() {
    if (csvMotionFrames) {
      return csvMotionFrames;
    }

    const csvText = await fetchText(motionCsvUrl);
    csvMotionFrames = parseMotionCsv(csvText);

    return csvMotionFrames;
  }

  function stopCsvMotion({ reset = false } = {}) {
    csvMotionRunning = false;
    csvMotionPaused = false;
    csvMotionStartedAt = 0;
    csvMotionPausedAt = 0;

    if (csvMotionRaf) {
      cancelAnimationFrame(csvMotionRaf);
      csvMotionRaf = null;
    }

    if (reset) {
      resetPose();
    }
  }

  function animateCsvMotion(now) {
    if (disposed || !csvMotionRunning || !csvMotionFrames?.length) {
      return;
    }

    if (csvMotionPaused) {
      csvMotionRaf = requestAnimationFrame(animateCsvMotion);
      return;
    }

    const frames = csvMotionFrames;
    const duration = frames[frames.length - 1].time;

    if (duration <= 0) {
      setPose(frames[0].pose);
      stopCsvMotion();
      return;
    }

    const elapsed = ((now - csvMotionStartedAt) / 1000) * csvMotionSpeed;
    let motionTime = elapsed;

    if (csvMotionLoop) {
      motionTime = elapsed % duration;
    } else if (elapsed >= duration) {
      setPose(frames[frames.length - 1].pose);
      stopCsvMotion();
      return;
    }

    const nextIndex = findMotionFrameIndex(frames, motionTime);
    const prevIndex = Math.max(0, nextIndex - 1);
    const pose = interpolatePose(frames[prevIndex], frames[nextIndex], motionTime);

    setPose(pose);

    csvMotionRaf = requestAnimationFrame(animateCsvMotion);
  }

  async function playCsvMotion(options = {}) {
    const {
      speed = 1,
      loop = true,
      reset = true,
      stopRandom = true,
    } = options;

    if (stopRandom) {
      stopMotionLoop();
    }

    const frames = await loadCsvMotionFrames();

    if (!frames.length) {
      throw new Error("CSV 모션 프레임이 없습니다.");
    }

    csvMotionSpeed = Math.max(0.01, Number(speed || 1));
    csvMotionLoop = loop !== false;
    csvMotionRunning = true;
    csvMotionPaused = false;

    if (reset) {
      setPose(frames[0].pose);
    }

    csvMotionStartedAt = performance.now();

    if (csvMotionRaf) {
      cancelAnimationFrame(csvMotionRaf);
      csvMotionRaf = null;
    }

    csvMotionRaf = requestAnimationFrame(animateCsvMotion);

    currentAnimationHandle = {
      stop() {
        stopCsvMotion({ reset: true });
      },
      pause() {
        pauseCsvMotion();
      },
      restart() {
        resumeCsvMotion();
      },
      play() {
        resumeCsvMotion();
      },
      setSpeedRatio(nextSpeed) {
        setCsvMotionSpeed(nextSpeed);
      },
    };

    currentAnimationName = "CSV Motion";
    updateMeshMetadata();

    return currentAnimationHandle;
  }

  function pauseCsvMotion() {
    if (!csvMotionRunning || csvMotionPaused) return;

    csvMotionPaused = true;
    csvMotionPausedAt = performance.now();
  }

  function resumeCsvMotion() {
    if (!csvMotionRunning) {
      playCsvMotion({
        speed: csvMotionSpeed,
        loop: csvMotionLoop,
        reset: false,
      });
      return;
    }

    if (!csvMotionPaused) return;

    const pausedDuration = performance.now() - csvMotionPausedAt;
    csvMotionStartedAt += pausedDuration;
    csvMotionPaused = false;
    csvMotionPausedAt = 0;
  }

  function setCsvMotionSpeed(speed = 1) {
    const nextSpeed = Math.max(0.01, Number(speed || 1));

    if (csvMotionRunning && !csvMotionPaused) {
      const now = performance.now();
      const elapsedSeconds = ((now - csvMotionStartedAt) / 1000) * csvMotionSpeed;
      csvMotionStartedAt = now - (elapsedSeconds / nextSpeed) * 1000;
    }

    csvMotionSpeed = nextSpeed;
    return csvMotionSpeed;
  }

  function playAnimation(payloadOrName, maybeOptions = {}) {
    const payload =
      typeof payloadOrName === "string"
        ? { name: payloadOrName, ...maybeOptions }
        : { ...(payloadOrName ?? {}) };

    currentAnimationName = payload.name ?? currentAnimationName ?? currentMission ?? "Default";
    currentAnimationLoop = payload.loop ?? currentAnimationLoop ?? true;

    setAnimationSpeed(payload.speed ?? currentAnimationSpeed ?? 1);
    stopCsvMotion({ reset: false });

    const profile = createAnimationProfile(currentAnimationName, currentMission);
    randomMotionPaused = false;

    if (!randomMotionRunning) {
      randomMotionRunning = true;
      scheduleNextTargets(profile);
      randomMotionRaf = requestAnimationFrame(animateRandomMotion);
    } else {
      scheduleNextTargets(profile);
    }

    updateMeshMetadata();

    currentAnimationHandle = {
      stop() {
        stopAnimation();
      },
      pause() {
        pauseAnimation();
      },
      restart() {
        resumeAnimation();
      },
      play() {
        resumeAnimation();
      },
      setSpeedRatio(speed) {
        setAnimationSpeed(speed);
      },
    };

    return currentAnimationHandle;
  }

  function stopAnimation() {
    stopMotionLoop();
    stopCsvMotion({ reset: true });
    currentAnimationHandle = null;
    updateMeshMetadata();
  }

  function pauseAnimation() {
    if (csvMotionRunning) {
      pauseCsvMotion();
      return;
    }

    if (!randomMotionRunning) return;
    randomMotionPaused = true;
  }

  function resumeAnimation() {
    if (disposed) return;

    if (csvMotionRunning) {
      resumeCsvMotion();
      return;
    }

    if (!randomMotionRunning) {
      playAnimation({
        name: currentAnimationName ?? currentMission ?? "Default",
        speed: currentAnimationSpeed,
        loop: currentAnimationLoop,
      });
      return;
    }

    randomMotionPaused = false;
    scheduleNextTargets(createAnimationProfile(currentAnimationName, currentMission));

    if (!randomMotionRaf) {
      animateRandomMotion._last = 0;
      randomMotionRaf = requestAnimationFrame(animateRandomMotion);
    }
  }

  function onKeyDown(event) {
    if (!enableKeyboard || disposed) return;

    const delta = 0.05;

    switch (event.key) {
      case "1":
        rotateJoint("shoulder_pan_joint", -delta);
        break;
      case "2":
        rotateJoint("shoulder_pan_joint", delta);
        break;
      case "3":
        rotateJoint("shoulder_lift_joint", -delta);
        break;
      case "4":
        rotateJoint("shoulder_lift_joint", delta);
        break;
      case "5":
        rotateJoint("elbow_joint", -delta);
        break;
      case "6":
        rotateJoint("elbow_joint", delta);
        break;
      case "7":
        rotateJoint("wrist_1_joint", -delta);
        break;
      case "8":
        rotateJoint("wrist_1_joint", delta);
        break;
      case "9":
        rotateJoint("wrist_2_joint", -delta);
        break;
      case "0":
        rotateJoint("wrist_2_joint", delta);
        break;
      case "-":
        rotateJoint("wrist_3_joint", -delta);
        break;
      case "=":
        rotateJoint("wrist_3_joint", delta);
        break;
      default:
        break;
    }
  }

  if (enableKeyboard) {
    window.addEventListener("keydown", onKeyDown);
  }

  if (enablePicking && scene.onPointerObservable) {
    pointerObserver = scene.onPointerObservable.add((pointerInfo) => {
      if (pointerInfo.type !== PointerEventTypes.POINTERPICK) return;

      const pickedMesh = pointerInfo.pickInfo?.pickedMesh;
      if (!pickedMesh) return;
      if (pickedMesh.metadata?.equipId !== equipId) return;

      scene.metadata = {
        ...(scene.metadata || {}),
        selectedRobotArmId: equipId,
      };
    });
  }

  updateMeshMetadata();

  if (animation?.autoPlay) {
    playAnimation(animation);
  }

  return {
    root,
    rootMesh: root,

    linkNodes,
    jointRuntime,

    meshes: loadedMeshes,
    visualMeshes,
    collisionMeshes,

    equipId,
    equipName,
    visualDebugInfo,
    motionDescription: descriptionsText,

    select,
    focusTarget,

    setJointAngle,
    rotateJoint,
    getPose,
    setPose,
    resetPose,

    playAnimation,
    stopAnimation,
    pauseAnimation,
    resumeAnimation,
    setAnimationSpeed,

    playCsvMotion,
    stopCsvMotion,
    pauseCsvMotion,
    resumeCsvMotion,
    setCsvMotionSpeed,

    setMode,
    setMission,

    setCollisionVisible,
    setCollisionEnabled,
    getVisualMeshes,
    getCollisionMeshes,

    dispose() {
      disposed = true;
      stopMotionLoop();
      stopCsvMotion({ reset: false });
      currentAnimationHandle = null;

      if (enableKeyboard) {
        window.removeEventListener("keydown", onKeyDown);
      }

      if (pointerObserver && scene.onPointerObservable) {
        scene.onPointerObservable.remove(pointerObserver);
        pointerObserver = null;
      }

      root?.dispose?.(false, true);
    },
  };
}

export default createRobotArmBinding;